import React, { useMemo } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import moment from "moment";

import { useSitesInMaintenance } from "./useSitesInMaintenance";

const useStyles = makeStyles(() => ({
    card: {
        width: "100%",
    },
    header: {
        backgroundColor: "#f5f5f5",
    },
    emptyState: {
        padding: "20px",
        textAlign: "center",
        color: "#666",
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        padding: "20px",
    },
    siteLink: {
        textDecoration: "none",
        color: "#1976d2",
        "&:hover": {
            textDecoration: "underline",
        },
    },
}));

interface Props {
    dealerId: number | null | undefined;
    orgId?: number | null | undefined;
    siteId?: number | null | undefined;
}

export function SitesInMaintenanceModeWidget({ dealerId, orgId, siteId }: Props) {
    const classes = useStyles();
    const { sites, loading } = useSitesInMaintenance({ dealerId, orgId, siteId });

    const sitesWithTimeRemaining = useMemo(() => {
        return sites.map(site => {
            // Parse endTime as UTC, then convert to local time for comparison
            const end = moment.utc(site.endTime).local();
            const now = moment();
            const duration = moment.duration(end.diff(now));

            let timeRemaining = "";
            if (duration.asMilliseconds() <= 0) {
                timeRemaining = "Ending soon";
            } else {
                const hours = Math.floor(duration.asHours());
                const minutes = duration.minutes();

                if (hours > 0) {
                    timeRemaining = `${hours}h ${minutes}m`;
                } else if (minutes > 0) {
                    timeRemaining = `${minutes}m`;
                } else {
                    timeRemaining = "< 1m";
                }
            }

            return {
                ...site,
                timeRemaining,
            };
        });
    }, [sites]);

    // Hide entire widget if no sites in maintenance (even while loading)
    if (sites.length === 0) {
        return null;
    }

    return (
        <Card className={classes.card}>
            <CardHeader
                className={classes.header}
                title="Sites in Maintenance"
                titleTypographyProps={{ variant: "h6" }}
            />
            <CardContent>
                {loading ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={32} />
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Dealer</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Site</TableCell>
                                <TableCell>Time Remaining</TableCell>
                                <TableCell>Enabled By</TableCell>
                                <TableCell>Comment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sitesWithTimeRemaining.map((site) => (
                                <TableRow key={site.id}>
                                    <TableCell>{site.dealerName || "-"}</TableCell>
                                    <TableCell>{site.organizationName || "-"}</TableCell>
                                    <TableCell>
                                        <Link
                                            to={`/dealers/${dealerId}/organizations/${site.organizationId}/sites/${site.siteId}/config`}
                                            className={classes.siteLink}
                                        >
                                            {site.siteName || `Site #${site.siteId}`}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{site.timeRemaining}</TableCell>
                                    <TableCell>{site.enabledByEmployeeName}</TableCell>
                                    <TableCell>
                                        {site.comment || "-"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
